import type { PAGE_POSITIONS } from 'types';
import type { ArticleCard as ArticleCardType } from 'types/capi/shared';

import ArticleCard from '~/components/ArticleCard';
import Feature from '~/modules/ArticlesSection/Feature';
import Grid from '~/modules/ArticlesSection/Grid';
import { NewsTiles } from '~/modules/ArticlesSection/Tiles';

export const HUB_MODULE_TYPES = {
  feature: 'news-feed-feature',
  grid: 'news-feed-grid',
  lead: 'news-feed-lead',
  tiles: 'news-feed-tiles'
};

const HUB_PAGE_POSITIONS: Record<
  keyof typeof HUB_MODULE_TYPES,
  PAGE_POSITIONS
> = {
  feature: 'News Feed Feature',
  grid: 'News Feed Grid',
  lead: 'News Feed Lead',
  tiles: 'News Feed Tiles'
};

const TILES_COUNT = 6;

type Props = {
  articles: ArticleCardType[];
  feature?: ArticleCardType;
  isFirstPage: boolean;
};

export default function NewsHub({ articles, feature, isFirstPage }: Props) {
  return isFirstPage ? (
    <FirstPage articles={articles} feature={feature} />
  ) : (
    <LaterPage articles={articles} />
  );
}

function modulePositions(renders: boolean[]) {
  let next = 1;

  return renders.map((rendered) => (rendered ? next++ : 0));
}

function FirstPage({
  articles,
  feature
}: {
  articles: ArticleCardType[];
  feature?: ArticleCardType;
}) {
  const [lead, ...rest] = articles;
  const hasFullTileRow = rest.length >= TILES_COUNT;
  const tiles = hasFullTileRow ? rest.slice(0, TILES_COUNT) : [];
  const grid = hasFullTileRow ? rest.slice(TILES_COUNT) : rest;
  const [leadPosition, tilesPosition, featurePosition, gridPosition] =
    modulePositions([
      Boolean(lead),
      hasFullTileRow,
      Boolean(feature),
      grid.length > 0
    ]);

  return (
    <>
      <Lead article={lead} modulePosition={leadPosition ?? 0} />
      <NewsTiles
        items={tiles}
        modulePosition={tilesPosition ?? 0}
        moduleType={HUB_MODULE_TYPES.tiles}
        pagePosition={HUB_PAGE_POSITIONS.tiles}
      />
      {feature ? (
        <Feature
          article={feature}
          modulePosition={featurePosition ?? 0}
          moduleType={HUB_MODULE_TYPES.feature}
          pagePosition={HUB_PAGE_POSITIONS.feature}
        />
      ) : null}
      <GridSection items={grid} modulePosition={gridPosition ?? 0} />
    </>
  );
}

function LaterPage({ articles }: { articles: ArticleCardType[] }) {
  const above = articles.slice(0, 9);
  const lead = articles[9];
  const below = articles.slice(10);
  const [abovePosition, leadPosition, belowPosition] = modulePositions([
    above.length > 0,
    Boolean(lead),
    below.length > 0
  ]);

  return (
    <>
      <GridSection items={above} modulePosition={abovePosition ?? 0} />
      <Lead article={lead} modulePosition={leadPosition ?? 0} />
      <GridSection items={below} modulePosition={belowPosition ?? 0} />
    </>
  );
}

function Lead({
  article,
  modulePosition
}: {
  article?: ArticleCardType;
  modulePosition: number;
}) {
  if (!article) return null;

  return (
    <div className="content-width py-6 md:py-10">
      <ArticleCard
        item={article}
        mdRatio="16:9"
        modulePosition={modulePosition}
        moduleType={HUB_MODULE_TYPES.lead}
        name="LATEST_NEWS"
        options={{ titleSize: 'lg' }}
        pagePosition={HUB_PAGE_POSITIONS.lead}
        position={1}
        variant="4/5-text-over"
      />
    </div>
  );
}

function GridSection({
  items,
  modulePosition
}: {
  items: ArticleCardType[];
  modulePosition: number;
}) {
  if (!items.length) return null;

  return (
    <div className="py-10 md:py-16">
      <Grid
        items={items}
        modulePosition={modulePosition}
        moduleType={HUB_MODULE_TYPES.grid}
        pagePosition={HUB_PAGE_POSITIONS.grid}
      />
    </div>
  );
}
